import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { machineDetailPath } from "../../constants/machineRoutes";
import { Button } from "reactstrap";
import OwnerDeviceSelection from "./components/owner-device-selection";
import Warranty from "../DeviceWarrantyRegistrationPage/components/warranty/index";
import UserInformationForm from "../DeviceWarrantyRegistrationPage/components/user-information-form";
import DeviceInformationForm from "./components/device-information-form/index";
import useAuthUser from "../../common/hooks/authUser";
import { useRegisterDevice } from "../../common/hooks/api/registration";
import { useSendTestEmail } from "../../common/hooks/api/email";
import { toastError, toastSuccess } from "../../helpers/toast-helper";
import Spinner from "../../components/Common/Spinner";
import { TUser } from "../../common/types/user";

const AdminWarrantyRegistrationPage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const { user, isAdmin, isDealer } = useAuthUser();
    const [formData, setFormData] = useState<any>({});
    const [selectedOwner, setSelectedOwner] = useState<TUser | null>(null);
    const [deviceSerialNumber, setDeviceSerialNumber] = useState<string>("");

    const { mutate: registerDevice, isPending: isRegistrationLoading } = useRegisterDevice();
    const { mutate: sendTestEmail, isPending: isSendingTestEmail } = useSendTestEmail();
    const isGlobalLoading = isRegistrationLoading || isSendingTestEmail;

    // Redirect if not admin or dealer
    useEffect(() => {
        if (user && !isAdmin && !isDealer) {
            toastError("Access denied. Admin or Dealer privileges required.");
            navigate("/");
        }
    }, [user, isAdmin, isDealer, navigate]);

    const onOwnerDeviceSelected = (data: { selectedOwner: TUser; deviceSerialNumber: string }) => {
        setSelectedOwner(data.selectedOwner);
        setDeviceSerialNumber(data.deviceSerialNumber);
        setFormData({
            ...formData,
            selectedOwner: data.selectedOwner,
            deviceSerialNumber: data.deviceSerialNumber,
        });
        setStep(1);
    };

    const onNext = (data?: any): void => {
        if (data) {
            setFormData({
                ...formData,
                ...data,
            });
        }
        setStep((prev) => prev + 1);
    };

    const onPrev = (data) => {
        if (data) {
            setFormData({
                ...formData,
                ...data,
            });
        }
        setStep((prev) => prev - 1);
    };

    const onSubmit = (data) => {
        const finalFormData = {
            ...formData,
            ...data,
        };

        const ownerEmail = selectedOwner?.email || user?.email;

        const payload = {
            userDetails: {
                user: {
                    email: ownerEmail,
                    firstName: finalFormData.user.firstName,
                    lastName: finalFormData.user.lastName,
                    phone: finalFormData.user.phoneNumber,
                },
                company: {
                    name: finalFormData.user.company,
                    businessType: finalFormData.user.businessType,
                    machineApplication: finalFormData.user.machineApplication,
                },
                address: {
                    line1: finalFormData.user.address,
                    line2: finalFormData.user.address2,
                    city: finalFormData.user.city,
                    state: finalFormData.user.state,
                    zip: finalFormData.user.zip,
                    country: finalFormData.user.country,
                }
            },
            deviceDetailsDTO: {
                serialNumber: deviceSerialNumber,
                deliveryDate: new Date(finalFormData.device.deliveryDate).toISOString(),
                hourMeter: finalFormData.device.hourMeter === '' ? undefined : finalFormData.device.hourMeter,
                saleCondition: finalFormData.device.isNew ? "New" : "Used",
                hadTransportDamage: finalFormData.device.hasShippingOrTransportDamage,
                hadOrderedOptions: finalFormData.device.allOptionsInstalled,
            },
            isAdminRegistration: true
        };
        
        registerDevice(payload, {
            onSuccess: async () => {
                await toastSuccess("Machine registration was finished successfully");
                navigate(machineDetailPath(deviceSerialNumber));
            },
            onError: async () => {
                toastError("Cannot update machine registration data");
            },
        });
    };

    const handleTestEmail = () => {
        const testEmail = user?.email || "";
        if (!testEmail) {
            toastError("No email address available for test email");
            return;
        }

        sendTestEmail(testEmail, {
            onSuccess: async () => {
                await toastSuccess(`Test email sent successfully to ${testEmail}`);
            },
            onError: async () => {
                toastError("Failed to send test email");
            },
        });
    };

    

    if (!isAdmin && !isDealer) {
        return null; // Will redirect in useEffect
    }

    return (
        <div
            className="position-relative"
            style={{
                minHeight: "60vh",
            }}
        >
            {isGlobalLoading && (
                <div style={{ position: "absolute", inset: 0, zIndex: 1000 }}>
                    <Spinner />
                </div>
            )}

            {isAdmin && (
                <div style={{ padding: "1rem", display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                    <Button
                        color="secondary"
                        onClick={handleTestEmail}
                        disabled={isSendingTestEmail || !user?.email}
                    >
                        {isSendingTestEmail ? "Sending..." : "Send Test Email"}
                    </Button>
                </div>
            )}

            {step === 0 && <OwnerDeviceSelection onNext={onOwnerDeviceSelected} />}
            {step === 1 && <Warranty onNext={onNext} deviceSerialNumber={deviceSerialNumber} />}
            {step === 2 && (
                <UserInformationForm
                    onNext={onNext}
                    onPrev={onPrev}
                    deviceSerialNumber={deviceSerialNumber}
                    user={selectedOwner}
                    data={formData?.user}
                    selectedOwner={selectedOwner}
                />
            )}
            {step === 3 && (
                <DeviceInformationForm
                    onNext={onSubmit}
                    onPrev={onPrev}
                    deviceSerialNumber={deviceSerialNumber}
                    data={formData?.device}
                    isRegistrationLoading={isRegistrationLoading}
                />
            )}
        </div>
    );
};

export default AdminWarrantyRegistrationPage;

